/* eslint react-hooks/exhaustive-deps: 0 */

import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Coordinates from '../Map/Coordinates';
import { isEmail } from '../../../../../../utils/regExpUtils';
import PickupPattern from './PickupPattern';
import { TemporaryReservationParams } from '../../../../../../services/taxi/TemporaryReservation/TemporaryReservationParams';
import luxon, { DateTime } from "luxon";
import { getRecaptchaToken } from '../../../../../../services/google/GoogleReCaptcha';
import { submitTemporaryReservation } from '../../../../../../services/taxi/TemporaryReservation';
import { getClosedGeocode } from '../../../../../../services/google/api';
import { useDispatch, useSelector } from 'react-redux';
import { switchisLoading, switchUseV2, updateErrorMessage, updateTaxiCompanyConfigurations } from '../../../../../../actions/generalAction';
import { isValidFLight } from '../../common/RegExp';
import { Location }  from '../../../../../../../src/services/google/PlaceDetail';

import scClick from '../../../../../common/ScClick';
import URLParams from '../../../../../../utils/getParams';
import Place from '../../../../../../services/google/Place';
import { convertLetter } from '../../common/Address';
import Cookies from 'js-cookie';
import { SelectPoint } from '../../../MiyakoTaxi/data/SeaGaiaNearbyFacilities';
import { getDirectionAndFareAPI} from '../../../../../../services/taxi/GetDirectionAndFare';
import { AlertLabel, convertStringToTaxiCompany, FlightType, GetDirectionAndFareOperation, GetDirectionAndFareParams, GetDirectionAndFareResponse, TaxiCompanyList } from '../../../../../../services/taxi/GetDirectionAndFare/models/getFareSamplesParams';
import { ComponentKey, getTaxiCompanyConfiguration, GetTaxiCompanyConfParams } from '../../../../../../services/taxi/GetTaxiMetaInfo';
import { DefaultFlightPattern, FlightPatternPeriod, FlightPatternSet, TaxiCompanyConfiguration, TaxiCompanyConfigurations, TaxiTimePattern } from '../../../../../../services/taxi/GetTaxiMetaInfo/model/taxiCompanyConf';
import Environment from '../../../../../../utils/environment';
import { CommonTaxiUtils } from '../../../CommonTaxi/CommonTaxiDateUtils';
import { useTranslation } from 'react-i18next';
import { changeLang, Lang } from '../../../../../../states/query';
import { changeLanguage } from '../../../../../../utils/lang';
import { getNowLuxonDateTime } from '../../../../../../utils/luxonUtil';
import { State } from '../../../../../../reducers';
import * as holiday from '@holiday-jp/holiday_jp';

/**
 * 搭乗便プルダウンの定義から、指定した乗車日に適用すべきセット（pickup/dropOff）を解決する。
 * - フラット形式（`{pickup,dropOff}`）はそのまま返す（従来互換）。
 * - 版形式（期間キーごとに start/end を持つ）は、乗車日が [start, end] に含まれる期間を選ぶ。
 *   どの期間にも一致しない場合は、定義順の先頭の期間へフォールバックする。
 * @param defaultFlightPattern 会社設定の defaultFlightPattern。
 * @param rideDate 乗車日時。
 */
const resolveFlightPatternSet = (
  defaultFlightPattern: DefaultFlightPattern | undefined,
  rideDate: luxon.DateTime,
): FlightPatternSet => {
  const empty: FlightPatternSet = { pickup: [], dropOff: [] };
  if (!defaultFlightPattern) {
    return empty;
  }
  // フラット形式（pickup が配列ならそのまま使う）。
  if (Array.isArray((defaultFlightPattern as FlightPatternSet).pickup)) {
    return defaultFlightPattern as FlightPatternSet;
  }
  // 版形式。乗車日が属する期間を選ぶ。
  const periods = Object.values(defaultFlightPattern as { [period: string]: FlightPatternPeriod });
  const rideMillis = rideDate.toMillis();
  const matched = periods.find((period) => {
    const afterStart = !period.start || rideMillis >= DateTime.fromISO(period.start).toMillis();
    const beforeEnd = !period.end || rideMillis <= DateTime.fromISO(period.end).toMillis();
    return afterStart && beforeEnd;
  });
  return matched ?? periods[0] ?? empty;
};

export interface BookingTaxiQueryParams {
  // 出発案内か
  isDeparture: boolean,
  // 発着地点名
  pointName: string,
  // 発着地点アドレス
  pointAddress: string,
  // 発着地点緯度
  pointLat: number,
  // 発着地点経度
  pointLng: number,
  // 発着空港コード
  airportCode: string,
  // 搭乗便ナンバー
  flightNumber: string,
  // フライトの発着時刻
  flightDate: luxon.DateTime,
  // タクシー乗車時刻
  rideDate: luxon.DateTime,
  // 国内線か
  isDomestic: boolean,
  // ターミナル名
  terminal: string,
  // 搭乗・到着ゲート名
  gate: string,
  // 連携タクシー
  company: TaxiCompanyList,
  // 連携パラメーター（宮交タクシーのシーガイア連携で用いる）
  mode: string,
  // 遷移元リファラー情報
  referrer: string,
  // 言語情報
  lang: string,
  // デバッグ用隠しパラメーター
  // 実際にメールを送らないか
  dryRun?: string,
  // 発着地点日本語アドレス
  pointJPAddress: string,
  // 発着地点日本語地点名
  pointJPName: string,
  // googleDirectionAPIに投げる出発時刻（フライト出発時刻に優越する）
  googleAPIDepDate: luxon.DateTime
}

export enum AirportTransferTaxiMapMode {
  // マップ選択モード
  map = 'map',
  // 選択モード
  select = 'select'
}

enum REFERRER_TYPE {
  /** ASWから仮予約画面に遷移 */
  asw = 'asw',
  /** 空港アクセスナビより遷移 */
  airportAccess = 'airportAccess',
  /** 北谷HPより遷移 */
  chatan = 'chatan',
  /** 福島空港サイトより遷移 */
  fukushima = 'fukushima'
}

/**
 * リファラータイプを返却する
 * @param referrer クエリから受け取ったリファラー値
 * @param taxiCompany 参照しているタクシー事業者
 * @returns
 */
export const getSCCkickType = (referrer?: string, taxiCompany?: TaxiCompanyList) => {
  // 結果画面からの経由で来たか判定
  // airport_access: 空港アクセスナビ・旅CUBEから来たと判定
  // chatan: タクシー会社が那覇ハイヤーの場合のみ、北谷MaaSサイトから来たと判定。
  // それ以外: ASWから来たと判定（実際には referrer=asw で飛んでくる）
  switch(referrer) {
    case 'airport_access':
    case 'tabicube':
      return REFERRER_TYPE.airportAccess;
    case 'chatan':
      if(taxiCompany === undefined) {
        return REFERRER_TYPE.chatan;
      }
      else if(taxiCompany === TaxiCompanyList.nahahire) {
        return REFERRER_TYPE.chatan;
      }
      else {
        return REFERRER_TYPE.asw;
      }
    case 'fukushima':
      return REFERRER_TYPE.fukushima;
    default:
      return REFERRER_TYPE.asw
  }
}

/** 未選択状態を識別する文字列 */
export const UNSELECTED_RIDING_DATE_STRING = '----------';

/**
 * タクシー予約画面の情報に関するカスタムフック。
 * @returns flightDate - 指定されたフライト日時。
 * @returns setFlightDate - フライト日時日時を更新する関数。
 * @returns email - メールアドレス。
 * @returns setEmail - メールアドレスを更新する関数。
 * @returns isEmptyEmail - メールアドレス欄が空であるかを表すフラグ。
 * @returns isInvalidEmail - 入力された文字列がメールアドレスの形式になっていないかを表すフラグ。
 */
export function useForm() {
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const useRecaptchaV2 = useSelector((state: State) => state.general.useV2);
  /** 乗車日時の初期値。（現在日時より5日後を設定する。） */
  const defaultDate = getNowLuxonDateTime().plus({ hours: 120 });

  const [ airportCode, setAirportCode ] = React.useState('HND');
  const [ center, setCenter ] = React.useState(Coordinates.TOKYO_STATION);
  const [ markerPoint, setMarkerPoint ] = React.useState(Coordinates.TOKYO_STATION);
  const [ pointName, setPointName ] = React.useState('');
  // 料金取得用日本語地点情報
  const [ placeJP, setPlaceJP ] = React.useState<Place|null>(null);
  const [ flight, setFlight ] = React.useState('');
  const [ flightDate, setFlightDate ] = React.useState<luxon.DateTime>(defaultDate);
  const [ ridingDate, setRidingDate ] = React.useState<luxon.DateTime>(defaultDate);
  const [ selectFlightData, setSelectFlightData ] = React.useState(UNSELECTED_RIDING_DATE_STRING);
  const [ isSubmitted, setSubmitted ] = React.useState(false);
  /** 予約操作時刻(reservationDatetime)の検証用上書き値(yyyyMMddHHmm形式)。dev/stg環境限定のデバッグ用。空文字なら実際の現在時刻を使用する。 */
  const [ debugReservationDatetime, setDebugReservationDatetime ] = React.useState('');

  /** 住所が空であるかを表すフラグ。 */
  const [ isEmptyAddress, setEmptyAddress ] = React.useState(false);

  const [ email, setEmail ] = React.useState('');
  /** メールアドレス欄が空であるかを表すフラグ。 */
  const [ isEmptyEmail, setEmptyEmail ] = React.useState(false);
  /** メールアドレスの形式になっていない文字列であるかを表すフラグ。 */
  const [ isInvalidEmail, setInvalidEmail ] = React.useState(false);
  /** 利用できないメールアドレスであるかを表すフラグ。 */
  const [ isUnavalableEmail, setUnavalableEmail] = React.useState(false);

  /** 便名が空であるかを表すフラグ。 */
  const [ isEmptyFlight, setEmptyFlight ] = React.useState(false);
  /** 便名の形式になっていない文字列であるかを表すフラグ。 */
  const [ isInvalidFlight, setInvalidFlight ] = React.useState(false);
  /** ユーザーに再試行してほしい（＝もう一度予約ボタンを押してもらいたい）場合であるかを表すフラグ。 */
  const [ isRequiredRetry, setRequiredRetry] = React.useState(false);
  /** 予約処理に失敗したかを表すフラグ。 */
  const [ isFailed, setFailed] = React.useState(false);
  /** 予約期限を過ぎているかを表すフラグ。 */
  const [ isOverDeadline, setOverdeadline ] = React.useState(false);
  /** 日付に関するアラートに表示するメッセージを管理する */
  const [ invalidFlightErrorMessage, setInvalidFlightErrorMessage ] = React.useState("");
  /** ご利用便日時に関するアラートに表示するメッセージを管理する */
  const [ invalidFlightDateErrorMessage, setInvalidFlightDateErrorMessage ] = React.useState("");
  /** reCaptcha v2での再検証を促すかを表すフラグ。 */
  const [ needRetryRecaptcha, switchNeedRetryRecaptcha ] = React.useState(false);
  /** 送迎タイプが許可されたパターンかを判定するフラグ */
  const [ disallowSupportDirection, switchDisallowSupportDirection ] = React.useState(false);
  /** 送迎タイプが便選択の場合、便選択がデフォルトかを判定するフラグ */
  const [ invalidFlightPattern, switchInvalidFlightPattern ] = React.useState(false);

  // TODO: やってることが pickupPattern とかぶるので後々削除する
  const [ toAirport, setToAirport ] = React.useState(true);
  const [ pickupPattern, setPickupPattern ] = React.useState(PickupPattern.TO_AIRPORT);

  /** 市区町村までの住所。 */
  const [municipality, setMunicipality] = React.useState('東京都千代田区');
  /** 送迎地点の地点情報 */
  const [place, setPlace] = React.useState<Place>();
  /** 空港送迎タクシーの対象エリアであるかを表すフラグ。 */
  const [isSubjectArea, switchSubjectArea] = React.useState(true);
  /** 距離の数値（m) */
  const [discance, setDistance] = React.useState(0);
  /** 距離の表示用ラベル */
  const [distanceText, setDistanceText] = React.useState('');
  /** 所要時間の数値(秒) */
  const [duaration, setDuration] = React.useState(0);
  /** 所要時間の表示ラベル */
  const [durationText, setDurationText] = React.useState('');
  /** 基本料金 */
  const [fee, setFee] = React.useState(0);
  /** トータルの料金 */
  const [totalFee, setTotalFee] = React.useState(0);
  /** 料金ラベル */
  const [feeLabel, setFeeLabel] = React.useState('');
  /** 注意事項ラベル */
  const [notesLabel, setNotesLabel] = React.useState<string[]>([]);
  /** アラートラベル */
  const [alertLabel, setAlertLabel] = React.useState<AlertLabel[]>([]);
  /** 対象となるタクシー会社 */
  const [taxiCompany, setTaxiCompany] = React.useState<TaxiCompanyList>(TaxiCompanyList.limoTaxi);

  /** リクエスト回数。 */
  const [requestCount, setRequestCount] = React.useState(1);

  /** 発着地点が編集済みであるかを表すフラグ。 */
  const [ isEdited, switchEdited ] = React.useState(false);
  /** 初期アクセス時にクエリから値を読み取ったかを判定するフラグ */
  const [ isEndInit, switchIsEndInit ] = React.useState(false);
  /** クエリパラメーター */
  const [ params, setParams ] = React.useState<BookingTaxiQueryParams|null>(null);
  /** Selectモードの際に選択した地点 */
  const [ selectPointNumber, setSelectPointNumber ] = React.useState("0");
  /** Selectモードの時の選択できる地点一覧 */
  const [ pointList, setPointList ] = React.useState<SelectPoint[]>([]);
  /** タクシー料金APIに問い合わせが完了しているか */
  const [ isEndCallTaxiAPI, switchIsEndCallTaxiAPI ] = React.useState(true);
  /** タクシー料金取得が失敗したか */
  const [ isFailCallTaxiAPI, switchIsFailCallTaxiAPI ] = React.useState(false);
  /** 対象のタクシー会社のメタデータ */
  const [ taxiCompanyConf, setTaxiCompanyConf ] = React.useState<TaxiCompanyConfiguration|null>(null);
  /** 対象空港リスト */
  const [ targetAirportCodeList, setTargetAirportCodeList ] = React.useState<string[]>([]);
  /** モード */
  const [ mode, setMode ] = React.useState<AirportTransferTaxiMapMode>(AirportTransferTaxiMapMode.map);
  /** 表示されている料金のタイプ */
  const [ fareType, setFareType ] = React.useState("");
  /** 料金のステータス情報（予約可能かどうか） */
  const [ isAvailableReservation, switchIsAvailableReservation ] = React.useState<boolean>(true);
  /** 言語情報 */
  const [ lang, setLang ] = React.useState<Lang>(Lang.ja);
  /** 初期状態で表示されるラベル */
  const [ defaultPointlabel, setDefaultPointLabel ] = React.useState<string>('');
  /** 日付選択タイプ */
  const [ timePattern, setTimePattern ] = React.useState<TaxiTimePattern>(TaxiTimePattern.normal);
  /** 選択可能な出発日時リスト */
  const [ selectTimeList, setSelectTimeList ] = React.useState<string[]>([UNSELECTED_RIDING_DATE_STRING]);
  /** 乗車日時が未選択状態かのフラグ */
  const [ isUnselectedRidingDate, switchIsUnselectedRidingDate ] = React.useState<boolean>(false);
  /** ご利用便日時（時間）が未選択状態かのフラグ */
  const [ isUnselectedFlightHour, switchIsUnselectedFlightHour ] = React.useState<boolean>(false);
  /** ご利用便日時（分）が未選択状態かのフラグ */
  const [ isUnselectedFlightMinutes, switchIsUnselectedFlightMinutes ] = React.useState<boolean>(false);
  /** ご利用便日時が未選択状態かのフラグ */
  const [ isUnselectedFlightDate, switchIsUnselectedFlightDate ] = React.useState<boolean>(false);
  /** referrerパラメーターの値。 */
  const [ referrer, setReferrer ] = React.useState<string>('');
  /** クエリの情報を元に料金情報を取得するか */
  const [ isInitGetFareInfo, switchIsInitGetFareInfo ] = React.useState<boolean>(true);
  /** 送迎に対応する便情報 */
  const [ flightPattern, setFlightPattern ] = React.useState<string[]>([]);
  /** 初回の空港変更かを判定するフラグ */
  const isFirstAirportChange = React.useRef(true);

  /** 予約操作時刻（yyyyMMddHHmm形式）を取得する共通関数。debugReservationDatetimeが設定されている場合はそちらを優先する(dev/stg環境限定のデバッグ用)。 */
  const getReservationDatetime = () => {
    if (debugReservationDatetime) {
      return debugReservationDatetime;
    }
    const now = getNowLuxonDateTime();
    return now.toFormat('yyyyMMddHHmm');
  }

  /** 象潟合同交通か？ */
  const isKisakataTaxi = [TaxiCompanyList.airport_liner, TaxiCompanyList.nikaho_go].includes(taxiCompany);
  /** 平日用のプルダウンリスト */
  const [ weekdayPointList, setWeekdayPointList ] = React.useState<SelectPoint[]>([]);
  /** 休日用のプルダウンリスト */
  const [ holidayPointList, setHolidayPointList ] = React.useState<SelectPoint[]>([]);
  /** 平日か休日か */
  const [ dayOfWeek, setDayOfWeek ] = React.useState<string>('');
  // 便接続の乗合タクシーかどうか
  const isFlightConnectionSharedTaxi = [TaxiCompanyList.sakataNo1Taxi, TaxiCompanyList.shokoHire, TaxiCompanyList.iwakiTaxi].includes(taxiCompany);

  /**
   * 象潟合同交通のプルダウンリストを設定する
   * @param params クエリパラメーター
   * @param ridingDate 乗車日時
   */
  const setNikahoPointListByParams = async (params: BookingTaxiQueryParams, ridingDate: luxon.DateTime, defaulPickupPattern: number) => {
    /**
     * プルダウンリストを取得する
     * @param id s3のjsonファイル名
     * @returns プルダウンリスト
     */
    const fetchPointList = async (id: 'nikahoWeekday' | 'nikahoHoliday') => {
      const response = await getTaxiCompanyConfiguration({
        companyCode: params.company,
        componentKey: ComponentKey.pulldown,
        id,
        language: lang
      });
    
      if (response.body !== null && Array.isArray(response.body) && response.body.length > 0) {
        return response.body;
      }
      return [];
    };

    const weekdayList = await fetchPointList('nikahoWeekday');
    const holidayList = await fetchPointList('nikahoHoliday');
    // 平日用のプルダウンリスト
    setWeekdayPointList(weekdayList);
    // 休日用のプルダウンリスト
    setHolidayPointList(holidayList);

    if (weekdayList.length > 0 && holidayList.length > 0) {
      // セレクトモードに切り替え
      setMode(AirportTransferTaxiMapMode.select);
      let currentList: SelectPoint[] = isHoliday(ridingDate) ? holidayList : weekdayList;
      // defaulPickupPatternが1(TO_AIRPORT)で平日の場合、id001を除外
      if (defaulPickupPattern === PickupPattern.TO_AIRPORT && !isHoliday(ridingDate)) {
        currentList = currentList.filter(point => point.id !== '001');
      }
      setDayOfWeek(isHoliday(ridingDate) ? 'holiday' : 'weekday');
      // プルダウンにセット
      setPointList(currentList);
    }
  };

  /** クエリのparamsとcompanyより選択地点の一覧を入れ込む */
  const setPointListByParams = async (params: BookingTaxiQueryParams) => {
    const setParams: GetTaxiCompanyConfParams = {
      companyCode: params.company,
      // TODO: 現状ではプルダウンだけなので固定値だが、今後風向きが変わったら対応する
      componentKey: ComponentKey.pulldown,
      id: params.mode,
      language: lang
    }
    getTaxiCompanyConfiguration(setParams)
    .then((res)=>{
      // Responseが配列で要素が入っている場合のみ処理する
      if(res.body !== null && Array.isArray(res.body) && res.body.length > 0) {
        const matchPointList: SelectPoint[] = res.body;
        setPointList(matchPointList);
        setMode(AirportTransferTaxiMapMode.select);
      }
    })
    .catch((e) => {
      console.error(e);
    })
  }

  /**
   * 【デバッグ専用】dummyVal の 地図⇔プルダウン 表示切替。
   * プルダウンへ切り替える際、選択肢(pointList)が未ロードなら取得してから切り替える。
   * （初期URLに mode パラメータが無く地図モードで開始した場合でもプルダウンを出せるようにするため）
   */
  const toggleDebugPulldownMode = async () => {
    if (mode === AirportTransferTaxiMapMode.select) {
      setMode(AirportTransferTaxiMapMode.map);
      return;
    }
    // プルダウンへ切り替える際は、お迎え先空港を宮崎(KMI)に強制する
    // （旧宮交シーガイアのプルダウンは宮崎空港向けのため、他空港だと料金が合わなくなる）
    setAirportCode('KMI');
    if (pointList.length === 0 && params) {
      // 選択肢が未取得ならロードする（setPointListByParams 内部で setMode(select) される）
      await setPointListByParams({ ...params, mode: params.mode || 'seaGaia' });
    } else {
      setMode(AirportTransferTaxiMapMode.select);
    }
  }

  /**
   * メールアドレス欄の値を変更されたときの処理。
   * @param e テキストボックスの値が変更されたときに受け渡されるイベント。
   */
  const onChangedEmail = (e: React.ChangeEvent<{
    name?: string | undefined;
    value: unknown;
  }>) => {
    /** テキストボックスに入力された値。 */
    const value = e.target.value as string;
    // メールアドレスとして更新する。
    setEmail(value);
    // メールアドレスが変更された場合は、メールアドレスが空であるフラグをオフにする。
    setEmptyEmail(false);
    // メールアドレスが変更された場合はリクエスト回数を1に戻す。
    setRequestCount(1);
  }

  /**
   * 搭乗便の値を変更されたときの処理。
   * @param e テキストボックスの値が変更されたときに受け渡されるイベント。
   */
  const onChangedFlight = (e: React.ChangeEvent<{
    name?: string | undefined;
    value: unknown;
  }>) => {
    /** テキストボックスに入力された値。 */
    const value = e.target.value as string;
    // 便名として更新する。
    setFlight(value);
    // 便名が変更された場合は、便名が空であるフラグをオフにする。
    setEmptyFlight(false);
  }


  /**
   * 空港が変更された場合の処理。
   * @param e セレクトボックスが変更されたときに受け渡されるイベント。
   */
  const onChangedAirport = (e: React.ChangeEvent<{
    name?: string | undefined;
    value: unknown;
  }>) => {
    /** セレクトボックスで選択された項目の値。（空港コード） */
    const value = e.target.value as string;
    // 選択された項目の値に空港コードを更新する。
    setAirportCode(value);
  }

  /**
   * 乗車日時の日付（「年」部分）が変更された場合の処理。
   * @param e セレクトボックスが変更されたときに受け渡されるイベント。
   */
  const onChangedRidingYear = (e: React.ChangeEvent<{
    name?: string | undefined;
    value: unknown;
  }>) => {
    /** セレクトボックスで選択された項目の値。 */
    const value = e.target.value as number;
    // セレクトボックスで変更された時刻どおりに乗車日時・フライト日時の「年」部分を設定する。
    const modifiedDate = ridingDate.set({
      year: value
    });
    // 乗車日時を変更後の日時に更新する。
    setRidingDate(modifiedDate);
  }

  /**
   * 乗車日時の日付（「月」部分）が変更された場合の処理。
   * @param e セレクトボックスが変更されたときに受け渡されるイベント。
   */
  const onChangedRidingMonth = (e: React.ChangeEvent<{
    name?: string | undefined;
    value: unknown;
  }>) => {
    /** セレクトボックスで選択された項目の値。 */
    const value: number = e.target.value as number;
    // セレクトボックスで変更された時刻どおりに乗車日時・フライト日時の「月」部分を設定する。
    const modifiedDate = ridingDate.set({
      month: value
    });
    // 乗車日時を変更後の日時に更新する。
    setRidingDate(modifiedDate);
  }

  /**
   * 乗車日時の日付（「日」部分）が変更された場合の処理。
   * @param e セレクトボックスが変更されたときに受け渡されるイベント。
   */
  const onChangedRidingDay = (e: React.ChangeEvent<{
    name?: string | undefined;
    value: unknown;
  }>) => {
    /** セレクトボックスで選択された項目の値。 */
    const value = e.target.value as number;
    // セレクトボックスで変更された時刻どおりに乗車日時・フライト日時の「日」部分を設定する。
    const modifiedDate = ridingDate.set({
      day: value
    });
    // 乗車日時を変更後の日時に更新する。
    setRidingDate(modifiedDate);
  }

  /**
   * 乗車日時の時刻（「時」部分）が変更された場合の処理。
   * @param e セレクトボックスが変更されたときに受け渡されるイベント。
   */
  const onChangedRidingHour = (e: React.ChangeEvent<{
    name?: string | undefined;
    value: unknown;
  }>) => {
    /** セレクトボックスで選択された項目の値。 */
    const value = e.target.value as number;
    /** セレクトボックスで変更された時刻どおりに「時」部分を設定する。 */
    const modifiedDate = ridingDate.set({
      hour: value
    });
    // 搭乗便の時刻を変更後の日時に更新する。
    setRidingDate(modifiedDate);
  }

  /**
   * 乗車日時の時刻（「分」部分）が変更された場合の処理。
   * @param e セレクトボックスが変更されたときに受け渡されるイベント。
   */
  const onChangedRidingMinutes = (e: React.ChangeEvent<{
    name?: string | undefined;
    value: unknown;
  }>) => {
    /** セレクトボックスで選択された項目の値。 */
    const value = e.target.value as number;
    /** セレクトボックスで変更された時刻どおりに「分」部分を設定する。 */
    const modifiedDate = ridingDate.set({
      minute: value
    });
    // 搭乗便の時刻を変更後の日時に更新する。
    setRidingDate(modifiedDate);
  }

  /**
   * 乗車日時の時刻（「時刻・分」部分）がプルダウンから選択されて変更された場合の処理。
   * @param e セレクトボックスが変更されたときに受け渡されるイベント。
   */
  const onChangedRidingHourAndMinutes = (e: React.ChangeEvent<{
    name?: string | undefined;
    value: unknown;
  }>) => {
    /** セレクトボックスで選択された項目の値。 */
    const value = e.target.value as string;
    /** 未選択状態を選んだ際の挙動 */
    if(value === UNSELECTED_RIDING_DATE_STRING) {
      /** 未選択状態をtrueにする */
      switchIsUnselectedRidingDate(true);
      return;
    }
    const hour = Number(value.slice(0, 2));
    const minutes = Number(value.slice(-2));
    /** セレクトボックスで変更された時刻どおりに「時」部分を設定する。 */
    const modifiedDate = ridingDate.set({
      hour: hour,
      minute: minutes
    });
    // 搭乗便の時刻を変更後の日時に更新する。
    setRidingDate(modifiedDate);
    /** 未選択状態を false にする */
    switchIsUnselectedRidingDate(false);
  }

  /**
   * ご利用便がプルダウンから選択されて変更された場合の処理。
   * @param e セレクトボックスが変更されたときに受け渡されるイベント。
   */
  const onChangedRidingFlight = (e: React.ChangeEvent<{
    name?: string | undefined;
    value: unknown;
  }>) => {
    /** セレクトボックスで選択された項目の値。 */
    const value = e.target.value as string;

    /** 未選択状態を選んだ際の挙動 */
    if (value === UNSELECTED_RIDING_DATE_STRING) {
      setSelectFlightData(value);
    }

    /** 選択されたフライト便の情報を処理する */
    const selectedFlight = flightPattern.find(flight => flight === value);
    if (selectedFlight) {
      setSelectFlightData(selectedFlight);
    }
  }

  /**
   * 搭乗便の時刻が変更された場合の処理共通部分
   */
  const onChangedFlightDateCommon = (e: React.ChangeEvent<{
    name?: string | undefined;
    value: unknown;
  }>, type: string) => {
    /** 未選択状態かのフラグ */
    const isUnselected = e.target.value === '---';
    if (type === 'hour') {
      switchIsUnselectedFlightHour(isUnselected);
    } else if (type === 'minutes') {
      switchIsUnselectedFlightMinutes(isUnselected);
    }
    if (isUnselected) return;
    // /** セレクトボックスで選択された項目の値。 */
    const value = e.target.value as number;
    /** セレクトボックスで変更された時刻どおりに「時」部分を設定する。 */
    const luxonSetObject = ((): luxon.DateObjectUnits => {
      switch(type) {
        case 'year':
          return {year: value};
        case 'month':
          return {month: value};
        case 'day':
          return {day: value};
        case 'hour':
          return {hour: value};
        case 'minutes':
        default:
          return {minute: value};
      }
    })()
    // 搭乗便の時刻を変更後の日時に更新する。
    setFlightDate(flightDate.set(luxonSetObject));
  }
  /**
   * 搭乗便の時刻が変更された場合の呼び出し口
   */
  const onChangedFlightDate = {
    // 搭乗便の時刻（「年」部分）が変更された場合の処理
    year: (e: React.ChangeEvent<{
      name?: string | undefined;
      value: unknown;
    }>) => {
      onChangedFlightDateCommon(e, 'year');
    },
    // 搭乗便の時刻（「月」部分）が変更された場合の処理
    month: (e: React.ChangeEvent<{
      name?: string | undefined;
      value: unknown;
    }>) => {
      onChangedFlightDateCommon(e, 'month');
    },
    // 搭乗便の時刻（「日」部分）が変更された場合の処理
    day: (e: React.ChangeEvent<{
      name?: string | undefined;
      value: unknown;
    }>) => {
      onChangedFlightDateCommon(e, 'day');
    },
    // 搭乗便の時刻（「時」部分）が変更された場合の処理
    hour: (e: React.ChangeEvent<{
      name?: string | undefined;
      value: unknown;
    }>) => {
      onChangedFlightDateCommon(e, 'hour');
    },
    // 搭乗便の時刻（「分」部分）が変更された場合の処理
    minutes: (e: React.ChangeEvent<{
      name?: string | undefined;
      value: unknown;
    }>) => {
      onChangedFlightDateCommon(e, 'minutes');
    }
  }

  /** 発着地情報。 */
  let points =  {
    label: pointName,
    latLng: `${center.lat},${center.lng}`,
  }

  /** 仮予約APIのリクエストパラメーターを取得する。 */
  const getSendParams = (token: string): TemporaryReservationParams => {
    // TODO: いらなさそうならこの辺削除する デフォルトチェックだけ残す
    const sendPoints = (()=>{
      if(points.label === defaultPointlabel) {
        throw new Error('座標がデフォルトのままです');
      }
      return points;
    })();
    // Selectモードの場合、選んだ選択肢を発着地点座標とラベルに用いる
    if(pointList.length > 0 && mode === AirportTransferTaxiMapMode.select && selectPointNumber !== '-1') {
      const selectPointObject = pointList[Number(selectPointNumber)];
      if(selectPointObject.name) {
        points = {
          label: selectPointObject.name,
          latLng: selectPointObject.latLng
        }
      }
    }
    // フライト情報
    let setFlightInfo: any = {
      number: flight,
      datetime: flightDate.toFormat('yyyyMMddHHmm'),
      // 空港着の場合は搭乗便の出発時間として、
      // 空港発の場合は搭乗便の到着時間として登録する。
      isDeparture: toAirport
    }
    // 国際・国内情報があれば連携する
    if(params?.isDomestic) {
      setFlightInfo = {
        ...setFlightInfo,
        flightType: params.isDomestic? FlightType.domestic: FlightType.international
      }
    }
    // ターミナル情報があれば連携する
    if(params?.terminal && params.terminal !== '') {
      setFlightInfo = {
        ...setFlightInfo,
        terminal: params.terminal
      }
    }
    // ゲート情報を連携する
    if(params?.gate && params.gate !== '') {
      setFlightInfo = {
        ...setFlightInfo,
        gate: params.gate
      }
    }
    // labelForPriceCalc で送る値は施設名を含めるようにする
    // クエリから取得した日本語住所情報
    const paramsPointJPAddress: string = params?.pointJPAddress || '';
    const jpAddress = placeJP? convertLetter(placeJP.getFullAddress().replace(/-/g, '_')): paramsPointJPAddress;
    const jpPlaceName = placeJP? ( placeJP.name || '' ): (params?.pointJPName || '');
    const labelForPriceCalc = jpPlaceName !== ''? `${jpAddress} ${jpPlaceName}`: jpAddress;

    // ★ 予約操作時刻を取得（共通関数を使用）
    const reservationDatetime = getReservationDatetime();

    let sendParams: TemporaryReservationParams = {
      mailaddress: email,
      pointInfo: {
        latLng: sendPoints.latLng,
        label: sendPoints.label,
        latLngForPriceCalc: sendPoints.latLng,
        // TODO: 今後宮交が英語版対応したときには、Selectで選んだ時の扱いを考える必要がある
        labelForPriceCalc: labelForPriceCalc
      },
      flight: setFlightInfo,
      taxiConnectFlightNumber: selectFlightData !== UNSELECTED_RIDING_DATE_STRING ? selectFlightData : undefined,
      rideDatetime: ridingDate.toFormat('yyyyMMddHHmm'),
      // ★ 予約操作時刻を付与
      reservationDatetime: reservationDatetime,
      // 空港着の場合はユーザーが指定した地点から乗る値（pickup）を、
      // 空港発の場合はユーザーが指定した地点で降りる（dropOff）を設定する。
      operation: toAirport ? "pickup" : "dropOff",
      token: token,
      // 空港送迎タクシーの値（limoTaxi）を設定する。
      company: taxiCompany,
      count: requestCount,
      referrer: params?.referrer || '',
      language: i18n.language,
      airportLetter: airportCode,
    };
    // reCAPTCHA v2で検証する場合のみ、vrパラメーターを含めてリクエストする。
    if (useRecaptchaV2) {
      sendParams = {
        ...sendParams,
        vr: useRecaptchaV2,
      }
    }
    // 本番環境以外テストパラメーターを追加する
    if(process.env['REACT_APP_STAGE_ENV'] !== 'master') {
      const backURL = `${window.location.protocol}//${window.location.host}/booking`;
      // localhostの場合には置換はしない
      if(!backURL.includes('localhost')) {
        // メールアドレスに書いてある戻り先URLを指定する
        // 現在アクセスしているURLをそのまま指定する
        sendParams.overwirteBookingUrl = backURL;
      }
      // 実際にメールを送らない
      if(params?.dryRun === 'true') {
        sendParams.dryRun = params?.dryRun === 'true';
      }
    }
    if(params && params.mode) {
      sendParams = {
        ...sendParams,
        mode: params.mode
      };
    }
    return sendParams;
  }

  /**
   * タクシー乗車が出来ない日時か
   * @param rideDate 乗車日時
   * @param taxiCompany タクシー会社
   * @returns エラーの場合にはtrueが返る
   */
  const checkRideDate = (rideDate: luxon.DateTime, taxiCompany: TaxiCompanyList, taxiConf = taxiCompanyConf) => {
    if(!taxiConf) {
      return true;
    }
    const limit = taxiConf.limit;

    // limitStartDaysが存在する場合
    if (limit.limitStartDays) {
      // 現在の日付
      const now = getNowLuxonDateTime();
      // 予約開始日を計算（乗車日から指定日数前）
      const startReservationDate = rideDate.minus({ days: limit.limitStartDays }).set({ hour: 0, minute: 0, second: 0 });
      // 現在の日付が予約開始日より前の場合はエラー
      if (now < startReservationDate) {
        setInvalidFlightErrorMessage(limit.errorMessage);
        return true;
      }
    }
    const taxiUtils = new CommonTaxiUtils(limit.limitDays, limit.limitHours, taxiCompany, airportCode);
    /** 指定乗車時刻がタクシーが予約可能期限内であるか */
    const isAvalableDate = !taxiUtils.isAvalable(rideDate);
    /** 指定乗車時刻がタクシーが予約可能期限内であるか */
    if(isAvalableDate) {
      const setErrorMessage = taxiUtils.getErrorMessage(limit.errorMessage, lang);
      setInvalidFlightErrorMessage(setErrorMessage);
    }
    return isAvalableDate;
  }

  /** 入力フォームで設定された各項目の検証をする。 */
  const validate = async() => {
    const emptyAddress = pointName === '';
    const emptyEmail = email === '';
    const emptyFlight = flight === '';
    const invalidEmail = email !== '' && !isEmail(email);
    const invalidFlight = flight !== '' && !isValidFLight(flight);
    // ご利用便日時が未選択状態の場合には、エラーにする
    const invalidFlightDate: boolean = (()=> {
      if (isUnselectedFlightHour || isUnselectedFlightMinutes) {
        setInvalidFlightDateErrorMessage(t('taxi:unselected_flight_date_error_msg'));
        return true;
      }
      return false;
    })();
    /** 不正な乗車日時である（現在日時より96時間を切っている）かを表すフラグ。 */
    const invalidRidingDate: boolean = (()=>{
      // ご利用便日時が未選択状態の場合には、乗車日時の検証をスキップする
      if(invalidFlightDate) {
        return false;
      }
      /** 未選択状態の場合には未選択エラーメッセージを表示する */
      if (isUnselectedRidingDate) {
        setInvalidFlightErrorMessage(t('taxi:unselected_riding_date_error_msg'));
        return true;
      }
      // sakataNo1Taxi または shokoHire 、iwakiTaxi の場合は処理をスキップ
      if (!isFlightConnectionSharedTaxi) {
        // to空港の場合にはフライト出発時刻が送迎時刻より前の場合にはアラートを出す
        if (toAirport && ridingDate > flightDate) {
          setInvalidFlightErrorMessage(t('taxi:set_after_time_dep'));
          return true;
        }
        // from空港の場合には、フライト到着時刻が送迎時刻より後の場合にエラーを出す
        if (!toAirport && ridingDate < flightDate) {
          setInvalidFlightErrorMessage(t('taxi:set_after_time_arr'));
          return true;
        }
      }
      return checkRideDate(ridingDate, taxiCompany);
    })();
    setEmptyAddress(emptyAddress);
    setEmptyEmail(emptyEmail);
    setEmptyFlight(emptyFlight);
    setInvalidEmail(invalidEmail);
    setInvalidFlight(invalidFlight);
    setOverdeadline(invalidRidingDate);
    switchIsUnselectedFlightDate(invalidFlightDate);
    // サポート送迎パターンがALLでないなら、許可されたタイプかの検証をする
    switch(taxiCompanyConf?.supportDirection) {
      case 'fromAirport':
          switchDisallowSupportDirection((pickupPattern !== PickupPattern.FROM_AIRPORT))
        break;
      case 'toAirport':
          switchDisallowSupportDirection((pickupPattern !== PickupPattern.TO_AIRPORT))
        break;
      default:
        switchDisallowSupportDirection(false);
    }
    /* 搭乗タクシーと対応フライトがある事業者の場合、対応フライトが選択できていないとエラーにする */
    const invalidFlightPattern: boolean = (()=> {
      if(flightPattern.length === 0) {
        return false;
      }
      return selectFlightData === UNSELECTED_RIDING_DATE_STRING;
    })();
    switchInvalidFlightPattern(invalidFlightPattern);

    if (
        emptyEmail ||
        emptyFlight ||
        invalidEmail ||
        invalidFlight ||
        invalidRidingDate ||
        invalidFlightDate ||
        emptyAddress ||
        isUnselectedRidingDate ||
        disallowSupportDirection ||
        invalidFlightPattern
      ) {
      return false;
    } else {
      return true;
    }
  }

  /** 本予約APIへのリクエストパラメーターを整形する。 */
  const modifyParams = (params: TemporaryReservationParams) => {
    // 本番環境ではテストパラメーターを削除する
    if(process.env['REACT_APP_STAGE_ENV'] === 'master') {
      delete params.overwirteBookingUrl;
      delete params.dryRun;
    }
    return params;
  }

  /**
   * 仮予約の処理。
   * @param tokenV2 reCAPTCHA v2のトークン。（reCAPTCHA v2を使用する場合のみ指定）
   * */
  const onSubmit = (tokenV2?: any) => {
    const clickName = (()=>{
      const scClicks = taxiCompanyConf?.scClick.temporaryReservation;
      const clickType = getSCCkickType(params?.referrer, taxiCompany);
      if(!scClicks) {
        return '';
      }
      return scClicks[clickType] || scClicks?.asw;
    })();
    // 本番以外ではメールアドレスをCookieに保存する
    if (Environment.getEnvironmentType() !== Environment.PRODUCTION) {
      const mailAddressCookieName = 'ana_airport_access_navi_email_address';
      Cookies.set(mailAddressCookieName, email);
    }
    // ダミータクシーの場合にはSCクリックを実行しない
    if(taxiCompany !== TaxiCompanyList.dummyVal) {
      // SCクリックの実行。
      scClick(clickName);
    }
    // 送信済み（予約ボタンを押した後）として扱うようフラグを更新。
    setSubmitted(true);
    // 処理中フラグをオンにする。
    dispatch(switchisLoading(true));
    // 非同期処理を実行するための無名関数の呼び出し。
    (async() => {
      /** Google reCapthcaから取得したトークン。 */
      const token = useRecaptchaV2 ? tokenV2 : await getRecaptchaToken();
      /** 仮予約APIに送信するパラメーター。 */
      let sendParams = getSendParams(token);
      /** 入力フォームの検証結果。 */
      const validatedResult = await validate();
      sendParams = modifyParams(sendParams);
      if (validatedResult) {
        // 仮予約APIにリクエストする。
        submitTemporaryReservation(sendParams).then((response) => {
          const statusCode = response?.status;
          /** 仮予約成功時に完了ページに送るパラメーター。 */
          const successParams = new URLSearchParams({
            company: taxiCompany,
            lang: lang,
            referrer: params?.referrer ? params.referrer : '',
          });
          switch (statusCode) {
            case 200:
              // 予約完了画面に遷移する。
              // タクシー会社を連携する
              history.push(`/booking-taxi/success?${successParams.toString()}`);
              break;
            case 403:
              // reCAPTCHA v3の検証に失敗した場合。
              // （エラーコードが100の場合はreCAPTCHA検証に失敗した場合のレスポンスであるため。）
              if (response?.data?.errorCode === 100) {
                // 予約ボタンの再クリック（reCAPTCHA v2での検証）を促すフラグをtrueにする。
                switchNeedRetryRecaptcha(true);
                // reCAPTCHA v2を使用するフラグをtrueにする。
                dispatch(switchUseV2(true));
              } else {
                // その他の場合は予約失敗フラグを設定する。
                setFailed(true);
              }
              break;
            case 499:
              // 別のメールアドレスを設定するよう促すフラグをtrueにする。
              setUnavalableEmail(true);
              break;
            case 599:
              // 予約処理の再実行を促すフラグをtrueにする。
              setRequiredRetry(true);
              // リクエスト回数をインクリメントする。
              setRequestCount(requestCount + 1);
              break;
            default:
              // 予約失敗フラグを設定する。
              setFailed(true);
              break;
          }
        }).catch(() => {
          // 何らかリクエスト処理に失敗した場合は、予約失敗フラグを設定する。
          setFailed(true);
          // 処理中フラグをオフにする。
        }).finally(() => {
          // 最後にかならず処理中フラグをオフにする。
          dispatch(switchisLoading(false));
        })
      } else {
        //  処理中フラグをオフにする。
        dispatch(switchisLoading(false));
        return;
      }
    })();
  }

  /** URLクエリパラメータを取得する */
  const getQueryParams = (searchParams: URLParams, getTaxiCompanyConf: TaxiCompanyConfigurations): BookingTaxiQueryParams | null => {
    // 連携されたクエリより、該当するタクシー会社を表示する
    const taxiCompany: TaxiCompanyList = convertStringToTaxiCompany(searchParams.getStringParam('company') || '') || TaxiCompanyList.limoTaxi;
    const taxiCompanyConf = getTaxiCompanyConf[taxiCompany] || getTaxiCompanyConf[TaxiCompanyList.limoTaxi];

    // 連携がない時に表示するデフォルト日付
    const defaultDate = (()=>{
      if(!taxiCompanyConf) {
        return getNowLuxonDateTime().plus({
          day: 4
        });
      }
      const limit = taxiCompanyConf.limit;
      const commonTaxiUtils = new CommonTaxiUtils(limit.limitDays, limit.limitHours, taxiCompany, airportCode);
      return commonTaxiUtils.getAvailableDate(getNowLuxonDateTime());
    })();
    const flightDate = (() => {
      const hasRideDate = searchParams.hasParam('rideDate');
      const hasFlightDate = searchParams.hasParam('flightDate');
      
      if (hasRideDate && !hasFlightDate) {
        // ご利用便日時が未選択状態の場合には、時間を未設定状態にする
        switchIsUnselectedFlightHour(true);
        switchIsUnselectedFlightMinutes(true);
        // rideDateの日付部分のみを使用して時刻は未設定状態にする
        const rideDateTime = searchParams.getLuxonDateTimeUnixTimeMilisecondsParam('rideDate', defaultDate);
        return rideDateTime
      }
      
      // 既存の処理を維持
      return searchParams.getLuxonDateTimeUnixTimeMilisecondsParam('flightDate', defaultDate);
    })();
    const rideDate = searchParams.getLuxonDateTimeUnixTimeMilisecondsParam('rideDate', defaultDate);
    let getParams: BookingTaxiQueryParams = {
      isDeparture:      searchParams.getBooleanParam('isDeparture'),
      pointAddress:     searchParams.getStringParam('pointAddress') || '',
      pointName:        searchParams.getStringParam('pointName') || '',
      pointLat:         searchParams.getFloatParam('pointLat'),
      pointLng:         searchParams.getFloatParam('pointLng'),
      airportCode:      searchParams.getStringParam('airportCode') || '',
      flightNumber:     searchParams.getStringParam('flightNumber') || '',
      flightDate:       flightDate,
      rideDate:         rideDate,
      isDomestic:       searchParams.getBooleanParam('isDomestic'),
      terminal:         searchParams.getStringParam('terminal') || '',
      gate:             searchParams.getStringParam('gate') || '',
      mode:             searchParams.getStringParam('mode') || '',
      referrer:         searchParams.getStringParam('referrer') || '',
      company:          taxiCompany,
      dryRun:           searchParams.getStringParam('dryRun') || undefined,
      lang:             searchParams.getStringParam('lang') || 'ja',
      pointJPAddress:   searchParams.getStringParam('pointJPAddress') || '',
      pointJPName:      searchParams.getStringParam('pointJPName') || '',
      googleAPIDepDate: searchParams.getLuxonDateTimeUnixTimeMilisecondsParam('googleAPIDepDate', rideDate),
    }

    // クエリに応じて言語の切り替えをします。
    switch(i18n.language) {
      case 'en':
        setLang(Lang.en);
        setDefaultPointLabel('Search here');
        setPointName('Search here');
        setPlaceJP(null);
        break;
      case 'ja':
      default:
        setLang(Lang.ja);
        setDefaultPointLabel('ここで検索');
        setPointName('ここで検索');
        setPlaceJP(null);
        break;
    }

    const targetAirportCodeList: string[] = taxiCompanyConf.supportAirports;
    // 空港コードだけ指定のコードリストに一致するか再度チェックを入れる
    if(!targetAirportCodeList.includes(getParams.airportCode)) {
      // 会社に合わせたデフォルトを入れる
      getParams.airportCode = taxiCompanyConf?.defaultAirportCode || '';
    }
    // 乗車日時が予約可能な範囲かチェックする
    const invalidRidingDate = checkRideDate(getParams.rideDate, taxiCompany, taxiCompanyConf);
    setOverdeadline(invalidRidingDate);
    if(invalidRidingDate) {
      const limit = taxiCompanyConf.limit;
      const commonTaxiUtils = new CommonTaxiUtils(limit.limitDays, limit.limitHours, taxiCompany, airportCode);
      const setErrorMessage = commonTaxiUtils.getErrorMessage(limit.errorMessage, lang);
      setInvalidFlightErrorMessage(setErrorMessage);
    }
    // 日本のエリア外である場合にはパラメーターを初期化させる
    if(!checkJPNArea(getParams.pointLat, getParams.pointLng)) {
      getParams = {
        ...getParams,
        pointLat: 0,
        pointLng: 0
      }
    }
    return getParams;
  }

  // 座標が日本周辺かを判定する
  // 日本周辺ならTrueを返す
  const checkJPNArea = (lat: number, lng: number) => {
    return lat > 20 && lat < 50 && lng < 155 && lng > 120;
  }

  /** 座標より住所情報を取得して入れ込める */
  const setMunicipalityFromPosition = async (position: Location) => {
    const latlng = `${position.lat},${position.lng}`;
    const places: Place[] = await getClosedGeocode(latlng, changeLang(i18n.language));
    if(places.length > 0) {
      const place: Place = places[0];
      setPlace(place);
      /** 「都道府県」・「市区町村」までの住所。 */
      const municipality = convertLetter(place.getAddressToMunicipality());
      setMunicipality(municipality);
    }
    else {
      // 位置情報が取れない場合には住所を空欄にする
      setMunicipality('')
    }
    return {
      'type': 'setMunicipalityFromPosition'
    }
  }

  // 送迎パターンが変更された場合、送迎パターンのフラグを変更する処理。
  React.useEffect(() => {
    setToAirport(PickupPattern.isToAirport(pickupPattern));
    /** 送迎パターンが変更されたとき対応フライトも初期値に戻す */
    setSelectFlightData(UNSELECTED_RIDING_DATE_STRING);
  }, [pickupPattern, setToAirport]);

  // 空港コードが変更された場合、地図の中心座標と地点の名称を変更する処理。
  React.useEffect(() => {
    // 発着地を未編集の場合のみ、名称・座標・市区町村を更新する。
    // クエリ読み込みが完了している場合のみ更新をかける
    if (isEndInit && taxiCompanyConf) {
      // 一度インクリメンタルサーチから候補を選んでいたら料金だけを変える
      if(isEdited) {
        getDirectionAndFare();
      }
      else {
        // パラメータ由来の空港変更検知だけはスキップする
        if(isFirstAirportChange.current) {
          // 2回目以降の手動変更に備えてフラグをFalseにする
          isFirstAirportChange.current = false;
          return;
        }
        // それ以外の場合には中心座標をデフォルトに変更する
        const defaultCenter = taxiCompanyConf.defaultCenter[airportCode];
        const newCenter = defaultCenter.position;
        // 初期地点名は「ここで検索」に設定
        setPointName(defaultPointlabel);

        // 空港に応じた初期座標に更新する。
        setCenter(newCenter);
        // マーカーポイントも更新する。
        setMarkerPoint(newCenter);
        /** 空港ごとに設定されている初期地点の市区町村。 */
        const municipalityByAirport = defaultCenter.municipality;
        // 空港ごとの初期値の市区町村に更新する。
        setMunicipality(municipalityByAirport);
        // 空港を切り替えて地点が変わらない場合には料金計算APIがそのままでは反応しないので手動で更新する
        if(center.lat === newCenter.lat && center.lng === newCenter.lng) {
          getDirectionAndFare();
        }
      }
    }
  }, [airportCode, setCenter, setPointName, taxiCompanyConf]);

  /**
   * 料金経路取得APIにリクエストする。
   * @param origin 出発地の緯度経度。
   * @param destination 目的地の緯度経度。
   */
  const getDirectionAndFare = React.useCallback(async() => {
    // エラーメッセージを空にする
    dispatch(updateErrorMessage(''));
    // 読込中をONにする
    switchIsEndCallTaxiAPI(false);
    // タクシー料金取得に失敗したかをFalseにする
    switchIsFailCallTaxiAPI(false);
    const useRideDate: luxon.DateTime = (()=>{
      if(isInitGetFareInfo && params?.googleAPIDepDate) {
        switchIsInitGetFareInfo(false);
        return params?.googleAPIDepDate;
      }
      return ridingDate;
    })();
    // ★ 予約操作時刻を取得（共通関数を使用）
    const reservationDatetime = getReservationDatetime();
    // クエリ連携がないデフォルト値を入れ込む
    let request: GetDirectionAndFareParams = {
      airportLetter: airportCode,
      operation: (pickupPattern === PickupPattern.TO_AIRPORT? GetDirectionAndFareOperation.pickup: GetDirectionAndFareOperation.dropOff),
      companyCode: taxiCompany,
      rideDatetime: useRideDate.toFormat('yyyyMMddHHmm'),
      // ★ 予約操作時刻を付与
      reservationDatetime: reservationDatetime,
      language: i18n.language
    }
    // クエリ連携がある場合には、フライトタイプ・ターミナル・ゲート情報を連携する
    if(params) {
      request = {
        ...request,
        flightType: params.isDomestic? FlightType.domestic: FlightType.international
      }
      // ターミナル情報が空出ない場合には入れ込む
      if(params.terminal !== '') {
        request = {
          ...request,
          terminal: params.terminal
        }
      }
      // 搭乗口情報が空出ない場合には入れ込む
      if(params.gate !== '') {
        request = {
          ...request,
          gate: params.gate
        }
      }
      // シーガイア識別用
      // if(params.mode === "seaGaia") {
      //   request = {
      //     ...request,
      //     mode: params.mode,
      //     modeValue: 'true'
      //   }
      // }
    }
    if(taxiCompany === TaxiCompanyList.nikaho_go && params) {
      const isHolidayDate = isHoliday(useRideDate);
      const newDayOfWeek = isHolidayDate ? 'holiday' : 'weekday';
      let usePointList = isHolidayDate ? holidayPointList : weekdayPointList;
      // 平日の空港行きの場合、id001を除外
      if (pickupPattern === PickupPattern.TO_AIRPORT && !isHolidayDate) {
        usePointList = usePointList.filter(point => point.id !== '001');
      }
      // 平日/休日が切り替わる時は必ずリセット
      if (dayOfWeek !== newDayOfWeek) {
        setDayOfWeek(newDayOfWeek);
        setPointList(usePointList);
        setSelectPointNumber('-1');
        setFeeLabel('');
        setDurationText('');
        setDistanceText('');
        setPointName(defaultPointlabel);
        switchIsEndCallTaxiAPI(true);
        return;
      }
      // 平日空港着の場合
      if (usePointList !== pointList) {
        const selectedPointId = pointList[Number(selectPointNumber)]?.id;
        const newListIndex = usePointList.findIndex(point => point.id === selectedPointId);
        setPointList(usePointList);
        if (newListIndex === -1) {
          setSelectPointNumber('-1');
          setFeeLabel('');
          setDurationText('');
          setDistanceText('');
          setPointName(defaultPointlabel);
          switchIsEndCallTaxiAPI(true);
          return;
        }
        setSelectPointNumber(String(newListIndex));
      }
    }
    // 便情報がある場合には便情報を設定する
    if(flightPattern.length > 0 && taxiCompanyConf !== null) {
      // 乗車日(ridingDate)に応じた版の便ダイヤを解決する
      const flightPatternSet = resolveFlightPatternSet(taxiCompanyConf.defaultFlightPattern, ridingDate);
      const newflightPattern = pickupPattern === PickupPattern.TO_AIRPORT?
        flightPatternSet.pickup:
        flightPatternSet.dropOff;
      setFlightPattern(newflightPattern)
    }
    // pointNameが「ここで検索」となっている場合にはエラーにする
    if(pointName === defaultPointlabel) {
      switchIsEndCallTaxiAPI(true);
      return;
    }
    // 発着地点情報を入れる
    const latlng = `${center.lat},${center.lng}`;
    // クエリから取得した住所情報
    const paramsPointAddress: string = params?.pointAddress || '';
    // クエリから取得した日本語住所情報
    const paramsPointJPAddress: string = params?.pointJPAddress || '';
    const setPointLabel = (() => {
      // 象潟合同の場合はセレクトボックスの選択値を使用
      if (isKisakataTaxi && pointList.length > 0 && selectPointNumber !== '-1') {
        const selectedPoint = pointList[Number(selectPointNumber)];
        return selectedPoint.name || '';
      }
      // それ以外は既存の処理を使用
      return convertLetter(place?.getFullAddress(lang) || paramsPointAddress);
    })();

    const setPointJPLabel = (() => {
      // 象潟合同の場合はセレクトボックスの選択値を使用
      if (isKisakataTaxi && pointList.length > 0 && selectPointNumber !== '-1') {
        const selectedPoint = pointList[Number(selectPointNumber)];
        return selectedPoint.name || '';
      }
      // それ以外は既存の処理を使用
      return convertLetter(place?.getFullAddress().replace(/-/g, '_') || paramsPointJPAddress);
    })();
    request.pointInfo = {
      latLng: latlng,
      label: setPointLabel,
      latLngForPriceCalc: latlng,
      labelForPriceCalc: setPointJPLabel
    }
    try {
      /** 料金情報の取得中は予約ボタンを押せないようにする */
      switchIsAvailableReservation(false);
      const response: GetDirectionAndFareResponse = await getDirectionAndFareAPI(request);
      if(response !== null) {
        // 料金ステータスを更新する
        switchIsAvailableReservation(response.status === 'available')
        // 距離ラベルを更新する
        setDistanceText(response.distanceLabel);
        // 所要時間ラベルを更新する
        setDurationText(response.durationLabel);
        // 基本料金を更新する。
        setFee(response.value);
        // 回送料金込の総額料金を更新する。
        setTotalFee(response.totalValue);
        // 料金ラベルを設定する
        setFeeLabel(response.fareLabel)
        // 距離の整数値が返ってくるようになったら入れ込む
        setDistance(response.distance)
        // 所要時間の整数値が返ってくるようになったら入れ込む
        setDuration(response.duration);
        // 料金タイプを入れ込む
        setFareType(response.fareType);
        // 注意事項ラベルを入れる
        if(response.notesLabel && response.notesLabel.length > 0) {
          setNotesLabel(response.notesLabel);
        }
        else {
          setNotesLabel([]);
        }
        // アラート情報を入れる
        if(response.alertLabel && response.alertLabel.length > 0 ) {
          setAlertLabel(response.alertLabel);
        }
        else {
          setAlertLabel([]);
        }
        // 必要であれば指定出発時刻リストを更新する
        if(response.timePattern) {
          const newSelectTimeList = [UNSELECTED_RIDING_DATE_STRING, response.timePattern].flat(1);
          const isSameTimeList = JSON.stringify(newSelectTimeList) === JSON.stringify(selectTimeList);
          if(!isSameTimeList) {
            /** 指定出発時刻リストを更新する */
            setSelectTimeList(newSelectTimeList);
            /** 未選択状態に変更する */
            switchIsUnselectedRidingDate(true);
          }
        }
        if(response.flightPattern) {
          setFlightPattern(response.flightPattern);
        }
      }
      else {
        throw new Error('料金算出APIでエラーが起きました')
      }
    }
    catch (e) {
      switchIsFailCallTaxiAPI(true);
      if(process.env['REACT_APP_STAGE_ENV'] === 'dev') {
        console.error(e);
      }
    }
    finally {
      switchIsEndCallTaxiAPI(true);
    }
  }, [center, ridingDate, airportCode, pickupPattern, taxiCompany, params, place]);

  // 送迎パターン、選択地点が変更されたときの処理。
  // TODO: 送迎パターンを変えると2回呼ばれるので対処する
  React.useEffect(() => {
    // クエリ読み込みが完了したときのみ更新をかける
    if(!isEndInit || !isEndCallTaxiAPI) {
      return;
    }
    // 料金経路取得APIにリクエストする
    getDirectionAndFare();
  }, [center, toAirport, isEndInit, ridingDate])

  // 【案A・検証用】dummyVal のプルダウンで地点を選択したら、選択地点の座標を center に、
  // 地点名を pointName に反映して料金を再計算させる。
  // （料金計算は center 依存で発火し、pointName === defaultPointlabel だと算出をスキップするため両方セットする）
  // seaGaia型のプルダウン料金計算がデッドコード化していた挙動の復活。dummyVal限定。
  React.useEffect(() => {
    if (taxiCompany === TaxiCompanyList.dummyVal
        && mode === AirportTransferTaxiMapMode.select
        && pointList.length > 0
        && selectPointNumber !== '-1') {
      const selectedPoint = pointList[Number(selectPointNumber)];
      if (selectedPoint?.latLng) {
        const [lat, lng] = selectedPoint.latLng.split(',').map(Number);
        if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
          setCenter({ lat, lng });
          setPointName(selectedPoint.name || '');
        }
      }
    }
  }, [selectPointNumber, mode, pointList, taxiCompany])

  // リクエスト回数に変更があった場合に発火する処理。
  React.useEffect(() => {
    // リクエスト回数が4回以上の場合。
    if (requestCount > 3) {
      // 使用できないメールアドレスであるフラグをオンにする。
      setUnavalableEmail(true);
    }
  }, [requestCount]);

  /** 住所情報が空かを判定する */
  React.useEffect(() => {
    const isEmptyAddress = (pointName === '' || pointName === defaultPointlabel);
    setEmptyAddress(isEmptyAddress);
    // 住所が空の時には予約ボタンを押せないようにする
    if(isEmptyAddress) {
      switchIsAvailableReservation(false);
    }
  }, [pointName])

  /** 日付が土日祝日かどうかを判定する */
  const isHoliday = (date: luxon.DateTime): boolean => {
    // holiday-jpを使って祝日判定
    const jsDate = date.toJSDate();
    const isPublicHoliday = holiday.isHoliday(jsDate);
    
    // 土日の判定
    const isWeekend = date.weekday === 6 || date.weekday === 7;
    
    return isPublicHoliday || isWeekend;
  }

  // 初回アクセス時のみクエリを読み込む
  React.useEffect(() => {

    const searchParams = new URLParams(location.search);

    // companyクエリが指定されているかチェック
    const companyParam = searchParams.getStringParam('company');

    // companyクエリが指定されていない場合、空港送迎ページのTOP画面にリダイレクト
    if (companyParam === null || companyParam === '') {
      window.location.replace('https://www.ana.co.jp/ja/jp/guide/prepare/airport-guide/access/airport-transfer/');
      return;
    }

    // 指定されたcompanyが有効な会社かチェック
    const validCompany = convertStringToTaxiCompany(companyParam);
    if (validCompany === null) {
      history.push('/404');
      return;
    }

    /** referrerパラメーターの値。 */
    const referrer = searchParams.getStringParam('referrer');
    // referrerパラメーターの値が取得できた場合のみ更新する。
    if (referrer !== null) {
      setReferrer(referrer);
    }
    // 言語設定をする
    changeLanguage(searchParams, i18n);
    const company: TaxiCompanyList = validCompany;
    // @ts-ignore
    const metaParams = {
      key: 'company_configuration',
      language: searchParams.getStringParam('lang') || 'ja',
      companyCode: company
    }
    // タクシー会社メタ情報を取得する
    getTaxiCompanyConfiguration(metaParams)
    .then((res)=>{
      // 情報が取れないときには何も処理をしない
      if(!res.body) {
        return;
      }
      const getTaxiCompanyConf: TaxiCompanyConfigurations = res.body;
      // Redux上にタクシーメタ情報を保存する
      dispatch(updateTaxiCompanyConfigurations(getTaxiCompanyConf));
      const getParams = getQueryParams(searchParams, getTaxiCompanyConf);
      // Stateに保存する
      setParams(getParams);
      // タクシー会社の保存をする
      // 連携パラメーターが無いときにはイースタンとして取り扱う
      const getTaxiCompany: TaxiCompanyList = getParams?.company? getParams.company: TaxiCompanyList.limoTaxi;
      setTaxiCompany(getTaxiCompany);
      setTaxiCompanyConf(getTaxiCompanyConf[getTaxiCompany]);
      /** 対応している空港一覧をメタ情報より取得する */
      const supportAirportCodeList = getTaxiCompanyConf[getTaxiCompany].supportAirports;
      setTargetAirportCodeList(supportAirportCodeList);
      const companyConf = getTaxiCompanyConf[getTaxiCompany];
      const defaultAirportCode: string = getParams?.airportCode || companyConf.defaultAirportCode;
      /** 日付選択タイプを設定する */
      setTimePattern(companyConf.timePattern);
      /** 日付選択タイプがプルダウンの場合、一旦選択しないと検索ができないようにする */
      if(companyConf.timePattern === TaxiTimePattern.pulldown) {
        switchIsUnselectedRidingDate(true);
      }
      if(getParams !== null) {
        // 空港コードの更新をする
        setAirportCode(getParams.airportCode);
        // フライトナンバーの設定をする
        setFlight(getParams.flightNumber)
        setEmptyFlight(false);
        // 搭乗便の時刻変更をする
        setFlightDate(getParams.flightDate);
        // 迎車時刻の変更をする
        setRidingDate(getParams.rideDate);
        /** デフォルトの送迎パターンを入れる */
        const defaulPickupPattern = (() => {
          switch(getTaxiCompanyConf[getTaxiCompany].supportDirection) {
            case 'toAirport':
              return PickupPattern.TO_AIRPORT;
            case 'fromAirport':
              return PickupPattern.FROM_AIRPORT;
            case 'all':
            default:
              return getParams.isDeparture? PickupPattern.TO_AIRPORT: PickupPattern.FROM_AIRPORT;
          }
        })();
        setPickupPattern(defaulPickupPattern);
        // アドレスがある場合にはアドレスを入れる
        if(getParams.pointAddress !== "") {
          let setDefaultPointName = getParams.pointAddress;
          // 地点名がある場合には追加で入れる
          if(getParams.pointName !== "") {
            setDefaultPointName = `${setDefaultPointName} ${getParams.pointName}`;
          }
          // クエリで連携された地点名を入れる
          setPointName(setDefaultPointName);
        }
        let position: Location = {
          lat: getParams.pointLat,
          lng: getParams.pointLng
        }
        // 連携クエリで座標が連携されている場合、読み込む
        // getQueryParams() 中で日本のArea内かを判定してる
        if(getParams.pointLat === 0 || getParams.pointLng === 0) {
          try {
            const defaultCenter = companyConf.defaultCenter[defaultAirportCode];
            position = defaultCenter.position;
          }
          catch(e) {
            if(process.env['REACT_APP_STAGE_ENV'] === 'dev') {
              console.debug(e);
            }
          }
        }
        // クエリで連携された地点名に更新する
        setCenter(position);
        // クエリで連携された地点にマーカーを建てる
        setMarkerPoint(position);
        // 非同期で座標より住所情報を取得して入れ込む
        setMunicipalityFromPosition(position)
        // Paramsによって選択モードかマップモードかを切り替える
        if( getParams.mode !== '' ) {
          // S3より選択肢一覧を取得する（非同期）
          setPointListByParams(getParams)
        }
        if (getParams.company === TaxiCompanyList.airport_liner) {
          getParams.mode = 'airportLiner';
          setPointListByParams(getParams);
          setSelectPointNumber('-1');
        }
        if (getParams.company === TaxiCompanyList.nikaho_go) {
          setNikahoPointListByParams(getParams, getParams.rideDate, defaulPickupPattern)
        }
        // デフォルトフライト選択要素があるならセレクトに追加する
        if(companyConf.defaultFlightPattern) {
          // 初期表示も乗車日(getParams.rideDate)に応じた版を解決する
          const flightPatternSet = resolveFlightPatternSet(companyConf.defaultFlightPattern, getParams.rideDate);
          const flightpattern = defaulPickupPattern === PickupPattern.TO_AIRPORT?
            flightPatternSet.pickup:
            flightPatternSet.dropOff;
          setFlightPattern(flightpattern)
        }
      }
    })
    .finally(()=>{
      // クエリがないときにはフラグをONにする
      switchIsEndInit(true);
    })
  }, []);

  return {
    airportCode, setAirportCode, onChangedAirport,
    center, setCenter,
    markerPoint, setMarkerPoint,
    pointName, setPointName,
    setPlaceJP,
    email, onChangedEmail, isEmptyEmail, isInvalidEmail,
    isUnavalableEmail, isRequiredRetry, isFailed,
    flight, onChangedFlight,
    isEmptyFlight, isInvalidFlight,
    flightDate, setFlightDate, onChangedFlightDate,
    ridingDate, setRidingDate, onChangedRidingYear, onChangedRidingMonth, onChangedRidingDay,
    onChangedRidingHour, onChangedRidingMinutes, onChangedRidingHourAndMinutes,
    isOverDeadline,
    toAirport, setToAirport, pickupPattern, setPickupPattern,
    isSubmitted, setSubmitted,
    onSubmit,
    municipality, setMunicipality,
    isSubjectArea, switchSubjectArea,
    distanceText, durationText,
    fee, totalFee,
    switchEdited,
    taxiCompany,
    setPlace,
    mode, setMode, toggleDebugPulldownMode,
    selectPointNumber, setSelectPointNumber,
    pointList,
    invalidFlightErrorMessage,
    feeLabel,
    isEndCallTaxiAPI,
    isFailCallTaxiAPI,
    targetAirportCodeList,
    isEndInit,
    fareType,
    notesLabel, alertLabel,
    isAvailableReservation,
    isEmptyAddress,
    taxiCompanyConf,
    lang,
    timePattern,
    flightPattern,
    selectFlightData,
    onChangedRidingFlight,
    selectTimeList,
    isUnselectedRidingDate,
    referrer,
    needRetryRecaptcha,
    switchNeedRetryRecaptcha,
    disallowSupportDirection,
    invalidFlightPattern,
    isUnselectedFlightHour,
    isUnselectedFlightMinutes,
    invalidFlightDateErrorMessage,
    isUnselectedFlightDate,
    debugReservationDatetime, setDebugReservationDatetime,
  }
}
